import { ORPCError } from "@orpc/client";

/**
 * What a failed API call says about itself. The API answers an error with its HTTP status and a
 * body `{ _tag, message }` (the area's `Schema.TaggedError`); the client surfaces it as an
 * `ORPCError` whose `data.body` is that body.
 */
export function apiErrorTag(error: unknown): string | undefined {
	if (!(error instanceof ORPCError)) return undefined;
	const body = (error.data as { body?: { _tag?: unknown } } | undefined)?.body;
	return typeof body?._tag === "string" ? body._tag : undefined;
}

export function apiErrorStatus(error: unknown): number | undefined {
	return error instanceof ORPCError ? error.status : undefined;
}

/**
 * No session, or it ended: the admin goes to the login page. (Not every 401: a passkey that fails
 * to sign in is a 401 `LoginFailed`, which the login page shows itself.)
 */
export const isUnauthenticated = (error: unknown): boolean =>
	apiErrorStatus(error) === 401 && apiErrorTag(error) === "Unauthenticated";

/** A 4xx: asking again will not help, so queries do not retry it. */
export const isClientError = (error: unknown): boolean => {
	const status = apiErrorStatus(error);
	return status !== undefined && status >= 400 && status < 500;
};

/** The error body's field `name` (`lockedUntil`, `attemptsLeft`), when the API sent one. */
export function apiErrorField(error: unknown, name: string): unknown {
	if (!(error instanceof ORPCError)) return undefined;
	const body = (error.data as { body?: Record<string, unknown> } | undefined)?.body;
	return body?.[name];
}
